/**
 * Boneca de Pano — bottom nav mobile (<768px)
 * Web component <bdp-bottom-nav>, light DOM, carregado com defer
 * por snippets/bottom-nav.liquid (depois de pubsub.js/constants.js).
 *
 * - Botão central abre o cart drawer nativo do Dawn (cart-drawer.open()
 *   com fallback pra click no #cart-icon-bubble e, por fim, /cart).
 * - Badge sincroniza com o carrinho: pubsub PUB_SUB_EVENTS.cartUpdate,
 *   evento DOM 'cart:update' e MutationObserver no #cart-icon-bubble
 *   (o Dawn re-renderiza a section cart-icon-bubble a cada mudança).
 * - Item ativo marcado por location.pathname.
 */

if (!customElements.get('bdp-bottom-nav')) {
  customElements.define(
    'bdp-bottom-nav',
    class BdpBottomNav extends HTMLElement {
      connectedCallback() {
        this.cartButton = this.querySelector('[data-bdp-cart-toggle]');
        this.badge = this.querySelector('[data-bdp-cart-count]');
        this.count = this.badge ? parseInt(this.badge.dataset.count, 10) || 0 : 0;

        this.onCartToggle = this.onCartToggle.bind(this);
        this.onCartEvent = this.onCartEvent.bind(this);

        this.cartButton?.addEventListener('click', this.onCartToggle);

        // Evento DOM genérico (outras partes do tema podem disparar)
        document.addEventListener('cart:update', this.onCartEvent);

        // Pubsub nativo do Dawn (pubsub.js + constants.js).
        // PUB_SUB_EVENTS é const top-level (binding lexical, não vira
        // window.PUB_SUB_EVENTS) — checar via typeof no identificador.
        if (
          typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined' &&
          PUB_SUB_EVENTS.cartUpdate
        ) {
          this.unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartEvent);
        }

        // O Dawn substitui o innerHTML do #cart-icon-bubble a cada mudança
        // de carrinho (renderContents) — sinal confiável pra sincronizar.
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble) {
          this.bubbleObserver = new MutationObserver(() => this.syncFromBubble());
          this.bubbleObserver.observe(bubble, { childList: true, subtree: true, characterData: true });
        }

        // Espelha o estado aberto/fechado do drawer no botão
        const drawer = document.querySelector('cart-drawer');
        if (drawer && this.cartButton) {
          this.cartButton.setAttribute('aria-expanded', drawer.classList.contains('active') ? 'true' : 'false');
          this.drawerObserver = new MutationObserver(() => {
            this.cartButton.setAttribute('aria-expanded', drawer.classList.contains('active') ? 'true' : 'false');
          });
          this.drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
        }

        this.markActive();
      }

      disconnectedCallback() {
        this.cartButton?.removeEventListener('click', this.onCartToggle);
        document.removeEventListener('cart:update', this.onCartEvent);
        if (this.unsubscribeCartUpdate) this.unsubscribeCartUpdate();
        this.bubbleObserver?.disconnect();
        this.drawerObserver?.disconnect();
        clearTimeout(this.pulseTimer);
      }

      /* ---------- Carrinho ---------- */

      onCartToggle() {
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') {
          if (drawer.classList.contains('active')) {
            drawer.close();
          } else {
            drawer.open(this.cartButton);
          }
          return;
        }

        // Fallback: cart-drawer.js já escuta o click no #cart-icon-bubble
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble) {
          bubble.click();
          return;
        }

        window.location.href = this.dataset.cartUrl || '/cart';
      }

      onCartEvent(payload) {
        const count = this.extractCount(payload);
        if (typeof count === 'number') {
          this.setCount(count);
          return;
        }
        // Evento DOM 'cart:update' sem payload numérico: busca direto no cart.js
        if (payload && payload.type === 'cart:update') {
          this.fetchCount();
          return;
        }
        // Pubsub sem contagem (ex.: add do product-form): se a bolha do header
        // existe, o Dawn a re-renderiza e o MutationObserver sincroniza;
        // senão, busca no cart.js
        if (!document.getElementById('cart-icon-bubble')) this.fetchCount();
      }

      extractCount(payload) {
        if (!payload) return undefined;
        const candidates = [];
        if (payload.detail) candidates.push(payload.detail.cartData, payload.detail);
        candidates.push(payload.cartData, payload);
        for (const source of candidates) {
          if (!source) continue;
          if (typeof source.item_count === 'number') return source.item_count;
          if (source.cart && typeof source.cart.item_count === 'number') return source.cart.item_count;
        }
        return undefined;
      }

      syncFromBubble() {
        const bubble = document.getElementById('cart-icon-bubble');
        if (!bubble) return;
        const countBubble = bubble.querySelector('.cart-count-bubble');
        if (!countBubble) {
          this.setCount(0);
          return;
        }
        const numberEl = countBubble.querySelector('span[aria-hidden="true"]');
        if (numberEl) {
          const parsed = parseInt(numberEl.textContent.trim(), 10);
          if (!isNaN(parsed)) this.setCount(parsed);
          return;
        }
        // Bolha sem número = 100+ itens (padrão do Dawn)
        this.setCount(100);
      }

      fetchCount() {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
          .then((response) => response.json())
          .then((cart) => {
            if (cart && typeof cart.item_count === 'number') this.setCount(cart.item_count);
          })
          .catch(() => {});
      }

      setCount(count) {
        if (typeof count !== 'number' || isNaN(count) || count < 0) return;
        const previous = this.count;
        this.count = count;

        if (this.badge) {
          this.badge.textContent = count > 9 ? '9+' : String(count);
          this.badge.dataset.count = String(count);
          this.badge.classList.toggle('bdp-bnav__badge--hidden', count === 0);
        }

        if (this.cartButton) {
          const label = count > 0 ? `Carrinho (${count} ${count === 1 ? 'item' : 'itens'})` : 'Carrinho';
          this.cartButton.setAttribute('aria-label', label);
        }

        if (count > previous) this.pulse();
      }

      pulse() {
        if (!this.cartButton) return;
        this.cartButton.classList.remove('bdp-bnav__cart--pulse');
        void this.cartButton.offsetWidth; // reinicia a animação
        this.cartButton.classList.add('bdp-bnav__cart--pulse');
        clearTimeout(this.pulseTimer);
        this.pulseTimer = setTimeout(() => {
          this.cartButton.classList.remove('bdp-bnav__cart--pulse');
        }, 700);
      }

      /* ---------- Item ativo ---------- */

      markActive() {
        const normalize = (path) => (path || '/').replace(/\/+$/, '') || '/';
        const path = normalize(window.location.pathname);
        const root = normalize((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/');

        const activeMap = {
          home: path === root,
          shop: path.indexOf('/collections') !== -1 || path.indexOf('/search') !== -1,
          account: path.indexOf('/account') !== -1,
          instagram: false,
        };

        this.querySelectorAll('[data-bdp-nav]').forEach((item) => {
          const isActive = Boolean(activeMap[item.dataset.bdpNav]);
          item.classList.toggle('bdp-bnav__item--active', isActive);
          if (isActive) {
            item.setAttribute('aria-current', 'page');
          } else {
            item.removeAttribute('aria-current');
          }
        });
      }
    }
  );
}
